import { supabase } from '../lib/supabase';

const BUCKET = 'site-documents';

export const siteDiaryService = {
  /**
   * Create a new diary entry in daily_site_records.
   */
  async createEntry(data, companyId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: record, error } = await supabase
        ?.from('daily_site_records')
        ?.insert({
          company_id: companyId,
          site_id: data?.siteId,
          record_date: data?.date,
          weather: data?.weather || null,
          labour_on_site: data?.labourOnSite || null,
          deliveries: data?.deliveries || null,
          work_completed: data?.workCompleted || null,
          issues_delays: data?.issuesDelays || null,
          general_notes: data?.generalNotes || null,
          submitted_by: user?.id,
          user_id: user?.id,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: record, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Get all diary entries for a specific site scoped by company.
   * Joins `daily_site_photos` so the expand-row UI can count and render them
   * without a separate round-trip. File paths are returned as-is; callers
   * should use getPhotoUrl to generate a short-lived signed URL on demand.
   */
  async getEntriesForSite(siteId, companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('daily_site_records')
        ?.select(`
          *,
          sites(site_name),
          user_profiles!user_id(full_name, email),
          daily_site_photos(id, file_path, uploaded_by)
        `)
        ?.eq('site_id', siteId)
        ?.eq('company_id', companyId)
        ?.order('record_date', { ascending: false });
      // NSherjFh PR 6: default-filters soft-deleted diary entries.
      // Pass `{ includeDeleted: true }` from the site-diary-management
      // 'View deleted' tab. Same opt-in shape as PRs 1-5.
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get all diary entries for a company (all sites).
   */
  async getAllEntries(companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('daily_site_records')
        ?.select(`
          *,
          sites(site_name),
          user_profiles!user_id(full_name, email),
          daily_site_photos(id, file_path, uploaded_by)
        `)
        ?.eq('company_id', companyId)
        ?.order('record_date', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get a single diary entry by ID, with signed URLs attached to each joined
   * photo row so the UI can render them immediately. The site-documents
   * bucket is private; callers must never rely on getPublicUrl.
   */
  async getEntryById(id, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('daily_site_records')
        ?.select(`
          *,
          sites(site_name),
          user_profiles!user_id(full_name, email),
          daily_site_photos(*)
        `)
        ?.eq('id', id);
      if (!includeDeleted) query = query?.is('deleted_at', null);
      // .maybeSingle so a soft-deleted id resolves to null gracefully
      // (caller renders not-found state) instead of throwing PGRST116.
      const { data, error } = await query?.maybeSingle();

      if (error) throw error;

      const photosWithUrls = await Promise.all(
        (data?.daily_site_photos || [])?.map(async (photo) => {
          if (!photo?.file_path) return { ...photo, signedUrl: null };
          const { data: urlData } = await supabase?.storage
            ?.from(BUCKET)
            ?.createSignedUrl(photo?.file_path, 3600);
          return { ...photo, signedUrl: urlData?.signedUrl || null };
        })
      );

      return { data: { ...data, daily_site_photos: photosWithUrls }, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * NSherjFh PR 6 — recoverable soft-delete on a diary entry.
   *
   * Built from scratch (no existing delete method or UI). Stamps
   * deleted_at + deleted_by on the daily_site_records row; idempotent
   * via .is('deleted_at', null) + .maybeSingle().
   *
   * The (site_id, user_id, record_date) UNIQUE was converted to a
   * partial unique index WHERE deleted_at IS NULL in the migration,
   * so the soft-delete frees up the date slot — a worker can re-
   * enter for the same day without conflict.
   *
   * daily_site_photos rows + the storage files in site-documents
   * are preserved. Photos cascade via the parent's deleted_at filter
   * on every reader (no per-photo soft-delete UI today).
   *
   * RLS: covered by the existing admin_site_manager_full_access_
   * daily_records FOR ALL policy (USING has no deleted_at predicate,
   * so the UPDATE permission applies to both soft-delete and restore).
   * Worker self-delete is RLS-permitted via workers_manage_own_daily
   * _records but the UI exposes Delete/Restore to admin only —
   * documented in the PR summary.
   */
  async deleteEntry(id) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('daily_site_records')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', id)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Inverse of deleteEntry. Clears deleted_at/_by so the entry
   * reappears across every active surface.
   *
   * Edge case: if the same (site, user, date) slot has been filled
   * by a new active entry while this one was soft-deleted, the
   * partial unique index conflicts on restore. The error bubbles
   * up unchanged (admin sees the unique-violation message) and the
   * admin resolves by soft-deleting the newer entry first. Rare in
   * practice given admin-only restore.
   */
  async restoreEntry(id) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('daily_site_records')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', id)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * NSherjFh PR 12 — hard-delete a single diary photo + its storage file.
   *
   * Storage-first sequence (per the brief):
   *   1. Read the row to grab file_path.
   *   2. storage.remove([file_path]) on the site-documents bucket.
   *   3. If storage remove fails → return error WITHOUT deleting the
   *      DB row. The state is recoverable (row still there, file still
   *      there, user retries).
   *   4. If storage remove succeeds → delete the DB row.
   *   5. If DB delete fails AFTER storage succeeded → log loudly and
   *      surface the error. State is inconsistent (file gone, row
   *      remains) but rare — admin sees a clear message and can
   *      manually clean up.
   *
   * The alternative — DB-first then storage — has a worse failure
   * mode: the DB row is gone but the file lingers in storage forever
   * (no row to look up the path), exactly the orphaned-file class
   * PR 11 surfaced for notes hard-delete.
   *
   * RLS: covered by existing FOR ALL policies on daily_site_photos:
   *   - admin_site_manager_full_access_diary_photos (admin/SM)
   *   - workers_manage_own_daily_photos (uploaded_by = auth.uid()
   *     OR admin)
   * No migration needed.
   */
  async deleteDailySitePhoto(photoId) {
    try {
      // Step 1: Read the row to get file_path. We need this BEFORE
      // deleting because the row is the only place that records
      // which storage object to remove.
      const { data: photo, error: readErr } = await supabase
        ?.from('daily_site_photos')
        ?.select('id, file_path')
        ?.eq('id', photoId)
        ?.maybeSingle();
      if (readErr) throw readErr;
      if (!photo) {
        // Row already gone (concurrent delete or stale UI). Treat as
        // success — the caller's intent ("make this photo go away")
        // is satisfied.
        return { error: null };
      }

      // Step 2: Remove the storage file. If file_path is empty for
      // some reason (orphaned row from a failed upload), skip the
      // storage call and proceed to delete the row.
      if (photo?.file_path) {
        const { error: storageErr } = await supabase?.storage
          ?.from(BUCKET)
          ?.remove([photo?.file_path]);
        if (storageErr) {
          // Recoverable: row + file both still there. User retries.
          console.error('[siteDiaryService] deleteDailySitePhoto storage remove failed:', {
            photoId,
            file_path: photo?.file_path,
            code: storageErr?.code,
            message: storageErr?.message,
          });
          return { error: storageErr };
        }
      }

      // Step 3: Storage gone (or never existed). Delete the DB row.
      // If this fails after storage succeeded, the file is gone but
      // the row remains — rare, but log it so an operator can clean
      // up the orphan row.
      const { error: deleteErr } = await supabase
        ?.from('daily_site_photos')
        ?.delete()
        ?.eq('id', photoId);
      if (deleteErr) {
        console.error('[siteDiaryService] deleteDailySitePhoto DB delete failed AFTER storage remove succeeded — orphan row:', {
          photoId,
          file_path: photo?.file_path,
          code: deleteErr?.code,
          message: deleteErr?.message,
        });
        return { error: deleteErr };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: error?.message } };
    }
  },

  /**
   * Upload a photo for a diary entry to Supabase Storage and record in daily_site_photos.
   */
  async uploadDiaryPhoto(file, entryId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `diary-photos/${entryId}/${Date.now()}_${Math.random()?.toString(36)?.slice(2)}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage
        ?.from(BUCKET)
        ?.upload(fileName, file, { upsert: false });

      if (uploadError) throw uploadError;

      const { data, error } = await supabase
        ?.from('daily_site_photos')
        ?.insert({
          daily_record_id: entryId,
          file_path: fileName,
          uploaded_by: user?.id,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Get a signed URL for a single diary photo. The site-documents bucket is
   * private, so getPublicUrl would return a URL that 403s. Signed URLs expire
   * after 1 hour. Shape matches clientService.getPhotoUrl: { url, error }.
   */
  async getPhotoUrl(filePath) {
    try {
      if (!filePath) return { url: null, error: { message: 'Missing file path' } };
      const { data, error } = await supabase?.storage
        ?.from(BUCKET)
        ?.createSignedUrl(filePath, 3600);
      if (error) throw error;
      return { url: data?.signedUrl || null, error: null };
    } catch (error) {
      return { url: null, error: { message: error?.message } };
    }
  },

  /**
   * Get signed URLs for multiple diary photos in parallel. Used by the list
   * view's expand-row to render thumbnails without N separate awaits at the
   * caller site.
   */
  async getPhotoUrls(filePaths) {
    try {
      const paths = (filePaths || [])?.filter(Boolean);
      if (paths?.length === 0) return { data: {}, error: null };
      const results = await Promise.all(
        paths?.map(async (path) => {
          const { data, error } = await supabase?.storage
            ?.from(BUCKET)
            ?.createSignedUrl(path, 3600);
          return [path, error ? null : (data?.signedUrl || null)];
        })
      );
      return { data: Object.fromEntries(results), error: null };
    } catch (error) {
      return { data: {}, error: { message: error?.message } };
    }
  },
};
